import { supabase } from '../lib/supabase';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const STORAGE_BUCKET = 'property-images';
const STORAGE_BUCKET_VIDEOS = 'property-videos';

// ============================================================
// Helpers internos
// ============================================================

/** Normaliza una propiedad de Supabase al formato que esperan los componentes */
function normalizeProperty(p) {
  const { property_images, property_features, property_videos, ...rest } = p;
  return {
    ...rest,
    imageUrls: (property_images || [])
      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
      .map(img => img.image_url),
    videoUrls: (property_videos || [])
      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
      .map(vid => vid.video_url),
    features: (property_features || []).map(f => f.feature),
  };
}

/** Normaliza un mensaje de contacto de snake_case a camelCase */
function normalizeMessage(m) {
  return {
    ...m,
    createdAt: m.created_at,
    propertyTitle: m.property_title,
    propertyId: m.property_id,
  };
}

/** Envuelve el dato en el formato { data: { data } } que usan todos los componentes */
function wrap(data) {
  return { data: { data, success: true } };
}

/** Lanza un error con el formato que espera el frontend */
function throwError(message, status = 400) {
  const err = new Error(message);
  err.response = { data: { message, success: false }, status };
  throw err;
}

/** Obtiene la URL pública de Storage de Supabase para una imagen */
function getStorageUrl(path) {
  return `${SUPABASE_URL}/storage/v1/object/public/${STORAGE_BUCKET}/${path}`;
}

/** Extrae el path relativo de una URL de Storage */
function extractStoragePath(imageUrl) {
  const prefix = `${SUPABASE_URL}/storage/v1/object/public/${STORAGE_BUCKET}/`;
  return imageUrl.startsWith(prefix) ? imageUrl.replace(prefix, '') : imageUrl;
}

/** Consulta de propiedad completa con imágenes, videos y características */
const PROPERTY_SELECT = '*, property_images(image_url, display_order), property_videos(video_url, display_order), property_features(feature)';

// ============================================================
// API PÚBLICA — Propiedades
// ============================================================
export const propertyApi = {
  getAll: async (params = {}) => {
    let query = supabase
      .from('properties')
      .select(PROPERTY_SELECT)
      .eq('status', 'DISPONIBLE')
      .order('created_at', { ascending: false });

    if (params.q) {
      query = query.or(`title.ilike.%${params.q}%,city.ilike.%${params.q}%,address.ilike.%${params.q}%`);
    }
    if (params.type) query = query.eq('type', params.type);
    if (params.minPrice) query = query.gte('price', parseFloat(params.minPrice));
    if (params.maxPrice) query = query.lte('price', parseFloat(params.maxPrice));
    if (params.minBedrooms) query = query.gte('bedrooms', parseInt(params.minBedrooms));
    if (params.city) query = query.ilike('city', `%${params.city}%`);

    const { data, error } = await query;
    if (error) throwError(error.message);
    return wrap((data || []).map(normalizeProperty));
  },

  getFeatured: async () => {
    const { data, error } = await supabase
      .from('properties')
      .select(PROPERTY_SELECT)
      .eq('featured', true)
      .eq('status', 'DISPONIBLE')
      .order('created_at', { ascending: false })
      .limit(6);
    if (error) throwError(error.message);
    return wrap((data || []).map(normalizeProperty));
  },

  getById: async (id) => {
    const { data, error } = await supabase
      .from('properties')
      .select(PROPERTY_SELECT)
      .eq('id', id)
      .single();
    if (error || !data) throwError('Propiedad no encontrada', 404);
    return wrap(normalizeProperty(data));
  },
};

// ============================================================
// API PÚBLICA — Contacto
// ============================================================
export const contactApi = {
  send: async (formData) => {
    const { error } = await supabase.from('contact_messages').insert({
      name: formData.name,
      email: formData.email,
      phone: formData.phone || null,
      subject: formData.subject || null,
      message: formData.message,
      property_id: formData.propertyId || null,
      property_title: formData.propertyTitle || null,
      read: false,
    });
    if (error) throwError('Error al enviar el mensaje. Intenta de nuevo.');
    return wrap({ sent: true });
  },
};

// ============================================================
// API ADMIN — Autenticación (usa Supabase Auth)
// ============================================================
export const adminApi = {
  login: async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throwError('Credenciales inválidas', 401);
    return wrap({
      token: data.session.access_token,
      username: data.user.email,
      expiresAt: new Date(data.session.expires_at * 1000).toISOString(),
    });
  },

  logout: async () => {
    await supabase.auth.signOut();
    return wrap(null);
  },

  validateToken: async () => {
    const { data } = await supabase.auth.getSession();
    const valid = Boolean(data?.session);
    return wrap({
      valid,
      username: data?.session?.user?.email || '',
      expiresAt: data?.session?.expires_at
        ? new Date(data.session.expires_at * 1000).toISOString()
        : '',
    });
  },

  // ---- Dashboard ----
  getStats: async () => {
    const [{ data: props }, { data: msgs }] = await Promise.all([
      supabase.from('properties').select('id, status'),
      supabase.from('contact_messages').select('id, read'),
    ]);
    return wrap({
      totalProperties: props?.length || 0,
      availableProperties: (props || []).filter(p => p.status === 'DISPONIBLE').length,
      totalMessages: msgs?.length || 0,
      unreadMessages: (msgs || []).filter(m => !m.read).length,
    });
  },

  // ---- Propiedades (admin ve todas, no solo disponibles) ----
  getAllProperties: async () => {
    const { data, error } = await supabase
      .from('properties')
      .select(PROPERTY_SELECT)
      .order('created_at', { ascending: false });
    if (error) throwError(error.message);
    return wrap((data || []).map(normalizeProperty));
  },

  createProperty: async (dto) => {
    const { features, imageUrls, ...fields } = dto;
    const { data: prop, error } = await supabase
      .from('properties')
      .insert({ ...fields, updated_at: new Date().toISOString() })
      .select()
      .single();
    if (error) throwError(error.message);

    if (features?.length) {
      await supabase.from('property_features').insert(
        features.map(f => ({ property_id: prop.id, feature: f }))
      );
    }

    const { data: full } = await supabase
      .from('properties')
      .select(PROPERTY_SELECT)
      .eq('id', prop.id)
      .single();
    return wrap(normalizeProperty(full || prop));
  },

  updateProperty: async (id, dto) => {
    const { features, imageUrls, ...fields } = dto;
    const { error } = await supabase
      .from('properties')
      .update({ ...fields, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throwError(error.message);

    // Actualizar features: borrar y reinsertar
    await supabase.from('property_features').delete().eq('property_id', id);
    if (features?.length) {
      await supabase.from('property_features').insert(
        features.map(f => ({ property_id: id, feature: f }))
      );
    }

    const { data: full } = await supabase
      .from('properties')
      .select(PROPERTY_SELECT)
      .eq('id', id)
      .single();
    return wrap(normalizeProperty(full));
  },

  deleteProperty: async (id) => {
    // Borrar imágenes de Storage antes de borrar el registro
    const { data: imgs } = await supabase
      .from('property_images')
      .select('image_url')
      .eq('property_id', id);
    if (imgs?.length) {
      const paths = imgs.map(i => extractStoragePath(i.image_url));
      await supabase.storage.from(STORAGE_BUCKET).remove(paths);
    }
    // Borrar videos de Storage
    const { data: vids } = await supabase
      .from('property_videos')
      .select('video_url')
      .eq('property_id', id);
    if (vids?.length) {
      const paths = vids.map(v => v.video_url.replace(`${SUPABASE_URL}/storage/v1/object/public/${STORAGE_BUCKET_VIDEOS}/`, ''));
      if (paths.length > 0) await supabase.storage.from(STORAGE_BUCKET_VIDEOS).remove(paths);
    }
    const { error } = await supabase.from('properties').delete().eq('id', id);
    if (error) throwError(error.message);
    return wrap({ deleted: true });
  },

  uploadImage: async (propertyId, file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    const allowed = ['jpg', 'jpeg', 'png', 'webp', 'gif'];
    const finalExt = allowed.includes(ext) ? ext : 'jpg';
    const path = `${propertyId}/${crypto.randomUUID()}.${finalExt}`;

    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session) throwError('No autenticado. Inicia sesión de nuevo.');

    const { error: uploadError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(path, file, { upsert: false, contentType: file.type });
    if (uploadError) throwError(`Error al subir imagen: ${uploadError.message}`);

    const publicUrl = getStorageUrl(path);

    // Calcular display_order
    const { data: existing } = await supabase
      .from('property_images')
      .select('display_order')
      .eq('property_id', propertyId)
      .order('display_order', { ascending: false })
      .limit(1);
    const nextOrder = existing?.[0] ? existing[0].display_order + 1 : 0;

    await supabase.from('property_images').insert({
      property_id: propertyId,
      image_url: publicUrl,
      display_order: nextOrder,
    });

    const { data: full } = await supabase
      .from('properties')
      .select(PROPERTY_SELECT)
      .eq('id', propertyId)
      .single();
    return wrap(normalizeProperty(full));
  },

  removeImage: async (propertyId, imageUrl) => {
    const path = extractStoragePath(imageUrl);
    await supabase.storage.from(STORAGE_BUCKET).remove([path]);
    await supabase.from('property_images')
      .delete()
      .eq('property_id', propertyId)
      .eq('image_url', imageUrl);

    const { data: full } = await supabase
      .from('properties')
      .select(PROPERTY_SELECT)
      .eq('id', propertyId)
      .single();
    return wrap(normalizeProperty(full));
  },

  uploadVideo: async (propertyId, file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    const allowed = ['mp4', 'webm', 'mov', 'avi', 'mkv', 'flv'];
    const finalExt = allowed.includes(ext) ? ext : 'mp4';
    const path = `${propertyId}/${crypto.randomUUID()}.${finalExt}`;

    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session) throwError('No autenticado. Inicia sesión de nuevo.');

    const { error: uploadError } = await supabase.storage
      .from(STORAGE_BUCKET_VIDEOS)
      .upload(path, file, { upsert: false, contentType: file.type });
    if (uploadError) throwError(`Error al subir video: ${uploadError.message}`);

    const publicUrl = `${SUPABASE_URL}/storage/v1/object/public/${STORAGE_BUCKET_VIDEOS}/${path}`;

    // Calcular display_order
    const { data: existing } = await supabase
      .from('property_videos')
      .select('display_order')
      .eq('property_id', propertyId)
      .order('display_order', { ascending: false })
      .limit(1);
    const nextOrder = existing?.[0] ? existing[0].display_order + 1 : 0;

    await supabase.from('property_videos').insert({
      property_id: propertyId,
      video_url: publicUrl,
      display_order: nextOrder,
    });

    const { data: full } = await supabase
      .from('properties')
      .select(PROPERTY_SELECT)
      .eq('id', propertyId)
      .single();
    return wrap(normalizeProperty(full));
  },

  removeVideo: async (propertyId, videoUrl) => {
    const path = videoUrl.replace(`${SUPABASE_URL}/storage/v1/object/public/${STORAGE_BUCKET_VIDEOS}/`, '');
    await supabase.storage.from(STORAGE_BUCKET_VIDEOS).remove([path]);
    await supabase.from('property_videos')
      .delete()
      .eq('property_id', propertyId)
      .eq('video_url', videoUrl);

    const { data: full } = await supabase
      .from('properties')
      .select(PROPERTY_SELECT)
      .eq('id', propertyId)
      .single();
    return wrap(normalizeProperty(full));
  },

  // ---- Mensajes (admin) ----
  getAllMessages: async () => {
    const { data, error } = await supabase
      .from('contact_messages')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throwError(error.message);
    return wrap((data || []).map(normalizeMessage));
  },

  getUnreadMessages: async () => {
    const { data, error } = await supabase
      .from('contact_messages')
      .select('*')
      .eq('read', false)
      .order('created_at', { ascending: false });
    if (error) throwError(error.message);
    return wrap((data || []).map(normalizeMessage));
  },

  markAsRead: async (id) => {
    const { data, error } = await supabase
      .from('contact_messages')
      .update({ read: true })
      .eq('id', id)
      .select()
      .single();
    if (error) throwError(error.message);
    return wrap(normalizeMessage(data));
  },

  deleteMessage: async (id) => {
    const { error } = await supabase.from('contact_messages').delete().eq('id', id);
    if (error) throwError(error.message);
    return wrap({ deleted: true });
  },
};

// ============================================================
// Helpers exportados (usados en componentes)
// ============================================================
export const formatPrice = (price, currency = 'USD') => {
  if (!price) return 'Precio a consultar';
  if (price < 10000) {
    return `${currency} ${price.toLocaleString('es-SV')}/mes`;
  }
  return `${currency} ${price.toLocaleString('es-SV')}`;
};

export const getPropertyTypeLabel = (type) => {
  const map = { CASA: 'Casa', APARTAMENTO: 'Apartamento', TERRENO: 'Terreno', COMERCIAL: 'Comercial', OFICINA: 'Oficina' };
  return map[type] || type || '—';
};

export const getPropertyStatusLabel = (status) => {
  const map = { DISPONIBLE: 'Disponible', VENDIDA: 'Vendida', ALQUILADA: 'Alquilada', RESERVADA: 'Reservada' };
  return map[status] || status || '—';
};

export const getStatusColor = (status) => {
  const map = { DISPONIBLE: 'green', VENDIDA: 'red', ALQUILADA: 'blue', RESERVADA: 'orange' };
  return map[status] || 'gray';
};

export const formatArea = (area) => {
  if (!area) return null;
  return `${area.toLocaleString('es-SV')} m²`;
};
